import { API_ENDPOINT } from 'src/constants';
import {
  PresignedUrlResponse,
  UploadStatusResponse,
  CustomersResponse,
  ApiErrorResponse,
  FileUploadRequest,
  UploadProgress,
  UploadedDataResponse,
} from '../types';
import { captureException } from 'src/libs/sentry';
import { getCurrentAuthToken } from 'src/contexts/AuthContext/utils';

const API_BASE_URL = API_ENDPOINT;

class CustomerDataUploadApiService {
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    // Get authentication token
    let token: string | undefined;
    try {
      token = await getCurrentAuthToken();
    } catch (error) {
      captureException({ error, fn: 'apiService.makeRequest' });
      throw error;
    }

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData: ApiErrorResponse = await response.json();
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    return response.json();
  }

  // Get presigned URL for file upload
  async getPresignedUrl(
    request: FileUploadRequest,
  ): Promise<PresignedUrlResponse> {
    const params = new URLSearchParams({
      customer: request.customerId,
      fileName: request.fileName,
    });

    return this.makeRequest<PresignedUrlResponse>(
      `/data/presignedurl?${params}`,
    );
  }

  // Upload file directly to S3 using presigned URL
  async uploadFileToS3(presignedUrl: string, file: File): Promise<void> {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to upload file to S3: ${response.statusText}`);
    }
  }

  // Get upload status for a file
  async getUploadStatus(
    customerId: string,
    fileKey: string,
  ): Promise<UploadStatusResponse> {
    const params = new URLSearchParams({
      customer: customerId,
      fileKey: fileKey,
    });

    return this.makeRequest<UploadStatusResponse>(
      `/data/uploadstatus?${params}`,
    );
  }

  // Get customers list
  async getCustomers(): Promise<CustomersResponse> {
    return this.makeRequest<CustomersResponse>('/data/customers');
  }

  // Get uploaded data by file ID
  async getUploadedData(
    customerId: string,
    fileId: string,
    dataType: string = 'all',
    from?: string,
    to?: string,
  ): Promise<UploadedDataResponse> {
    const params = new URLSearchParams({
      customer: customerId,
      fileId: fileId,
      dataType: dataType,
    });

    if (from) {
      params.append('from', from);
    }
    if (to) {
      params.append('to', to);
    }

    return this.makeRequest<UploadedDataResponse>(
      `/data/uploaded-data?${params}`,
    );
  }

  // Poll upload status until completion
  async pollUploadStatus(
    customerId: string,
    fileKey: string,
    onProgress?: (progress: UploadProgress) => void,
  ): Promise<UploadProgress> {
    const maxAttempts = 10;
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const response = await this.getUploadStatus(customerId, fileKey);
        const status = response.data[0];

        if (status) {
          const progress: UploadProgress = {
            fileKey: status.fileKey,
            fileId: status.fileId,
            status: status.status as
              | 'pending'
              | 'processing'
              | 'succeeded'
              | 'failed',
            errorMessage: status.errorMessage,
            notifications: status.notifications,
          };

          onProgress?.(progress);

          if (progress.status === 'succeeded' || progress.status === 'failed') {
            return progress;
          }
        }
      } catch (error) {
        captureException({ error, fn: 'pollUploadStatus' });
      }

      // Wait 5 seconds before next poll (regardless of success/failure)
      await new Promise((resolve) => setTimeout(resolve, 5000));
      attempts++;
    }

    throw new Error('Upload status polling timed out');
  }
}

export const customerDataUploadApiService = new CustomerDataUploadApiService();
